import { useState, useEffect } from "react";
import { Layout } from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { format, parseISO, isPast } from "date-fns";
import { es } from "date-fns/locale";
import { Calendar, Clock, Video, User } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEGAL_DISCLAIMER = "Este servicio es de acompañamiento y escucha. No es un servicio médico y no sustituye atención profesional.";

export default function UserDashboard() {
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      const response = await axios.get(`${API}/sessions/my`);
      setSessions(response.data);
    } catch (error) {
      console.error("Error fetching sessions:", error);
    } finally {
      setLoading(false);
    }
  };

  const upcomingSessions = sessions.filter(s => {
    const sessionDate = parseISO(`${s.date}T${s.time}`);
    return !isPast(sessionDate) && s.status !== 'cancelled';
  });

  const pastSessions = sessions.filter(s => {
    const sessionDate = parseISO(`${s.date}T${s.time}`);
    return isPast(sessionDate) || s.status === 'cancelled';
  });

  return (
    <Layout>
      <div className="container-main py-6 md:py-12">
        <div className="max-w-4xl mx-auto">
          <div className="mb-6 md:mb-8">
            <h1 className="text-2xl md:text-3xl font-serif font-bold text-foreground">Hola, {user?.name}</h1>
            <p className="mt-2 text-stone-600 text-sm md:text-base">Gestiona tus sesiones de acompañamiento</p>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner"></div>
            </div>
          ) : (
            <div className="space-y-6 md:space-y-8">
              {/* Upcoming Sessions */}
              <section>
                <h2 className="text-xl font-serif font-semibold text-foreground mb-4">Próximas sesiones</h2>
                {upcomingSessions.length === 0 ? (
                  <div className="bg-white rounded-2xl border border-stone-200 p-8 text-center">
                    <Calendar className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                    <p className="text-stone-600">No tienes sesiones próximas</p>
                    <Button className="mt-4 btn-primary" onClick={() => window.location.href = '/explorar'} data-testid="explore-listeners-btn">
                      Explorar listeners
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {upcomingSessions.map((session) => (
                      <div key={session.id} className="bg-white rounded-2xl border border-stone-200 p-6" data-testid={`upcoming-session-${session.id}`}>
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-4">
                            <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                              <User className="w-6 h-6 text-primary" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-foreground">{session.listener_name}</h3>
                              <div className="mt-1 flex items-center gap-4 text-sm text-stone-600">
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-4 h-4" />
                                  {format(parseISO(session.date), "EEEE d 'de' MMMM", { locale: es })}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Clock className="w-4 h-4" />
                                  {session.time}
                                </span>
                              </div>
                            </div>
                          </div>
                          {session.google_meet_link && (
                            <a 
                              href={session.google_meet_link} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="btn-secondary flex items-center gap-2 px-4 py-2"
                              data-testid={`join-session-${session.id}`}
                            >
                              <Video className="w-4 h-4" />
                              Unirse
                            </a>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              {/* Past Sessions */}
              {pastSessions.length > 0 && (
                <section>
                  <h2 className="text-xl font-serif font-semibold text-foreground mb-4">Sesiones anteriores</h2>
                  <div className="space-y-4">
                    {pastSessions.map((session) => (
                      <div key={session.id} className="bg-stone-50 rounded-2xl border border-stone-200 p-6 opacity-75" data-testid={`past-session-${session.id}`}>
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 bg-stone-200 rounded-full flex items-center justify-center">
                            <User className="w-6 h-6 text-stone-400" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-foreground">{session.listener_name}</h3>
                            <div className="mt-1 flex items-center gap-4 text-sm text-stone-500">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                {format(parseISO(session.date), "d 'de' MMMM, yyyy", { locale: es })}
                              </span>
                              <span className="flex items-center gap-1">
                                <Clock className="w-4 h-4" />
                                {session.time}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              <p className="legal-disclaimer text-center pt-8">
                {LEGAL_DISCLAIMER}
              </p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
